import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
  type SortingState,
  type PaginationState,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ChevronLeft, ChevronRight, RefreshCw, X } from 'lucide-react';
import { toast } from 'sonner';
import { AsyncSearchSuggestions, type SearchSuggestion } from '@/components/ui/async-search-suggestions';
import { Skeleton } from '@/components/ui/skeleton';
import type { MaterialRequestDto } from '../types';
import { DocumentStatus } from '../types';
import { columns } from './material-request-columns';
import { useMaterialRequestStore } from '../store';
import { materialRequestService } from '../services/materialRequestService';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

interface MaterialRequestTableProps {
  data: MaterialRequestDto[];
  isLoading: boolean;
  totalCount: number;
  /** Externally managed sorting state (URL-persisted) */
  sorting: SortingState;
  onSortingChange: (updater: SortingState | ((prev: SortingState) => SortingState)) => void;
  /** Externally managed pagination state (URL-persisted) */
  pagination: PaginationState;
  onPaginationChange: (updater: PaginationState | ((prev: PaginationState) => PaginationState)) => void;
  /** Total pages from server */
  pageCount: number;
  /** Called after sync completes — re-fetches current page */
  onRefresh: () => void;
}

export function MaterialRequestTable({
  data,
  isLoading,
  totalCount,
  sorting,
  onSortingChange,
  pagination,
  onPaginationChange,
  pageCount,
  onRefresh,
}: MaterialRequestTableProps) {
  const navigate = useNavigate();
  const { isSyncing, syncFromNetSuite, lastSyncResult } = useMaterialRequestStore();
  const [syncFromDate, setSyncFromDate] = useState('');

  const getMrsSuggestions = useCallback(async (query: string): Promise<SearchSuggestion[]> => {
    const results = await materialRequestService.search(query);
    return results.map((mr) => {
      const statusLabel =
        mr.documentStatus === DocumentStatus.Closed ? 'Served'
        : mr.documentStatus === DocumentStatus.Cancelled ? 'Cancelled'
        : 'Open';
      return {
        id: mr.id,
        title: mr.mrsNumber,
        subtitle: `${mr.requestorFullName || mr.requestorIdNumber} · ${statusLabel}`,
        href: `/mro/issuance/mrs/${mr.id}`,
      };
    });
  }, []);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    manualSorting: true,
    pageCount,
    autoResetPageIndex: false,
    state: { sorting, pagination },
    onSortingChange,
    onPaginationChange,
    meta: { onRefresh },
  });

  const handleSync = async () => {
    const result = await syncFromNetSuite(syncFromDate || undefined);
    // 86d48d39v: a sync reports success even when individual MRS were rejected
    // (e.g. an over-long NetSuite line description). Surface the first error the
    // same way the single-MRS resync does, so the rejection is not swallowed.
    if (result && result.errors.length > 0) {
      toast.warning(result.errors[0]);
    }
    if (result?.success) {
      onRefresh();
    }
  };

  const { pageIndex, pageSize } = pagination;
  const canPreviousPage = pageIndex > 0;
  const canNextPage = pageIndex < pageCount - 1;

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2">
        <AsyncSearchSuggestions
          getSuggestions={getMrsSuggestions}
          placeholder="Search by MRS#, requestor name, or employee ID..."
          className="w-80"
          inputClassName="h-9"
        />
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">
            {totalCount > 0
              ? `${totalCount.toLocaleString()} request${totalCount !== 1 ? 's' : ''} total`
              : 'No requests'}
          </span>
          {lastSyncResult && (
            <span className="text-sm text-muted-foreground">
              {(() => {
                const parts: string[] = [];
                if (lastSyncResult.newRecordsCreated > 0) parts.push(`${lastSyncResult.newRecordsCreated} new`);
                if (lastSyncResult.updatedExisting > 0) parts.push(`${lastSyncResult.updatedExisting} updated`);
                if (lastSyncResult.reopened > 0) parts.push(`${lastSyncResult.reopened} reopened`);
                if (lastSyncResult.statusChanged > 0) parts.push(`${lastSyncResult.statusChanged} status changed`);
                if (lastSyncResult.flagged > 0) parts.push(`${lastSyncResult.flagged} flagged`);
                // 86d48d39v: `failed` was omitted, so a sync whose only outcome was
                // rejected records fell through to '· Up to date' — the reported symptom.
                if (lastSyncResult.failed > 0) parts.push(`${lastSyncResult.failed} failed`);
                return parts.length > 0 ? `· ${parts.join(', ')}` : '· Up to date';
              })()}
            </span>
          )}
          <div className="flex items-center gap-1.5">
            <div className="relative">
              <Input
                type="date"
                value={syncFromDate}
                onChange={(e) => setSyncFromDate(e.target.value)}
                className="h-9 w-[150px] text-sm"
                placeholder="Start date"
                title="Set a custom sync start date (leave empty for auto)"
                disabled={isSyncing || isLoading}
              />
              {syncFromDate && (
                <button
                  type="button"
                  onClick={() => setSyncFromDate('')}
                  className="absolute right-7 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  title="Clear date (use auto sync)"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <Button
              variant="outline"
              onClick={handleSync}
              disabled={isSyncing || isLoading}
            >
              <RefreshCw
                className={`h-4 w-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`}
              />
              {isSyncing ? 'Syncing...' : syncFromDate ? 'Sync From Date' : 'Sync Now'}
            </Button>
          </div>
        </div>
      </div>

      {/* Table — flat: row dividers only, no outer box */}
      <div>
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {/* First-load only: skeleton rows in the body. On refetch with existing
                rows, keep the stale data visible instead of blanking to a skeleton. */}
            {isLoading && data.length === 0 ? (
              Array.from({ length: pageSize }).map((_, r) => (
                <TableRow key={`skeleton-${r}`}>
                  {columns.map((_col, c) => (
                    <TableCell key={c}>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => navigate(`/mro/issuance/mrs/${row.original.id}`)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No material requests found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination controls */}
      <div className="flex items-center justify-between px-2">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>Rows per page</span>
          <Select
            value={String(pageSize)}
            onValueChange={(value) =>
              onPaginationChange((prev) => ({ ...prev, pageSize: Number(value), pageIndex: 0 }))
            }
          >
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-4">
          <span className="text-sm text-muted-foreground">
            Page {pageCount === 0 ? 0 : pageIndex + 1} of {pageCount}
          </span>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                onPaginationChange((prev) => ({ ...prev, pageIndex: prev.pageIndex - 1 }))
              }
              disabled={!canPreviousPage || isLoading}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                onPaginationChange((prev) => ({ ...prev, pageIndex: prev.pageIndex + 1 }))
              }
              disabled={!canNextPage || isLoading}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
